import { Injectable } from '@angular/core';
import { HashMap, TranslateParams, TranslocoService } from '@jsverse/transloco';
import { translate as pulseTranslateFn } from '@rantalainen/node-pulse-client/browser';

export type FlowSupportedLanguage = 'fi' | 'sv' | 'en';

@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  constructor(public translocoService: TranslocoService) {}

  /**
   * Gets the instant translated value of a key (from transloco translations)
   *
   * @example
   *
   * translate<string>('hello')
   * translate('hello', { value: 'value' })
   * translate<string[]>(['hello', 'key'])
   * translate('hello', { }, 'en')
   * translate('scope.someKey', { }, 'en')
   */
  translate<T = string>(key: TranslateParams, params?: HashMap, lang?: string): T {
    return this.translocoService.translate<T>(key, params, lang);
  }

  /** Proxy for transloco service getActiveLang */
  getActiveLang(): FlowSupportedLanguage {
    return this.translocoService.getActiveLang() as FlowSupportedLanguage;
  }

  getActiveLangLocalesArgument(): Intl.LocalesArgument {
    const lang = this.getActiveLang();

    return lang === 'sv' ? 'sv-SE' : lang === 'en' ? 'en-GB' : 'fi-FI';
  }

  setActiveLang(lang: FlowSupportedLanguage): void {
    this.translocoService.setActiveLang(lang);
  }

  /** Proxy for translations provided by node-pulse-client's translate feature */
  pulseTranslate(value: any): string {
    // node-pulse-client only supports 'fi' | 'sv'; English falls back to Finnish for Pulse-provided labels.
    const lang = this.getActiveLang() === 'sv' ? 'sv' : 'fi';

    return pulseTranslateFn(value, lang);
  }

  /** Translates the given property from an object using default `translations` object logic */
  translateInputValue(inputObject: any, inputKey: string): string {
    const lang = this.getActiveLang();

    // for example: object.translations.name.sv
    const translation = inputObject?.translations?.[inputKey]?.[lang];

    return translation || inputObject?.[inputKey];
  }
}
